import { useEffect, useRef, useState } from "react";
import {
  GetCommunicationAudienceMemberCount,
} from "../../api/communicationService";

export const AudienceCountState = {
  Pending: 0,
  Complete: 1,
  Error: 2,
  NotFound: 3,
};
export const POLL_TIMEOUT_MS = 5 * 60 * 1000;

export function getAudienceCountDelayMs(iteration: number) {
  if (iteration < 5) return 1000;
  if (iteration < 10) return 2000;
  return 5000;
}

/** Polls the count endpoint until it resolves; returns the count, or null on error/timeout. */
export async function pollAudienceCount(
  dispatch: unknown,
  requestId: string
): Promise<number | null> {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const poll = async (iteration: number) => {
      if (Date.now() - startTime >= POLL_TIMEOUT_MS) {
        resolve(null);
        return;
      }
      const response = await GetCommunicationAudienceMemberCount(dispatch, requestId);
      if (!response || response instanceof Error || !response.data) {
        resolve(null);
        return;
      }
      const { State, Count } = response.data;
      if (State === AudienceCountState.Complete) {
        resolve(Count);
      } else if (State === AudienceCountState.Error || State === AudienceCountState.NotFound) {
        // No count is available (errored, or the request id is stale/expired).
        resolve(null);
      } else {
        setTimeout(() => poll(iteration + 1), getAudienceCountDelayMs(iteration + 1));
      }
    };

    setTimeout(() => poll(0), getAudienceCountDelayMs(0));
  });
}

interface UseAudienceMemberCountProps {
  dispatch: unknown;
  requestId: string | null | undefined;
  initialCount: number | null | undefined;
  onResolved?: (count: number) => void;
}

/** Polls when a request id is present (reporting via onResolved), otherwise reflects initialCount. */
export function useAudienceMemberCount({
  dispatch,
  requestId,
  initialCount,
  onResolved,
}: UseAudienceMemberCountProps) {
  const [count, setCount] = useState(initialCount);
  const [counting, setCounting] = useState(!!requestId);

  // keep count in sync with a changing initialCount while we're not polling
  useEffect(() => {
    if (!requestId) {
      setCount(initialCount);
    }
  }, [initialCount, requestId]);

  // avoid re-triggering the poll when onResolved changes identity
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;

  useEffect(() => {
    if (!requestId) {
      setCounting(false);
      return;
    }

    let cancelled = false;
    setCounting(true);

    pollAudienceCount(dispatch, requestId).then((result) => {
      if (cancelled) return;
      setCounting(false);
      if (result !== null) {
        setCount(result);
        if (onResolvedRef.current) {
          onResolvedRef.current(result);
        }
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch, requestId]);

  return { count, counting };
}
